import React from 'react';

interface FilterBarProps {
  allTags: string[];
  selectedTags: string[];
  searchQuery: string;
  onTagToggle: (tag: string) => void;
  onSearchChange: (query: string) => void;
  onClearFilters: () => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  allTags,
  selectedTags,
  searchQuery,
  onTagToggle,
  onSearchChange,
  onClearFilters,
}) => {
  const hasActiveFilters = selectedTags.length > 0 || searchQuery.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-4 mb-4">
      {/* Search Bar */}
      <div className="mb-4">
        <label htmlFor="search" className="block text-xs font-medium text-gray-700 mb-1">
          Search Projects
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            id="search"
            type="text"
            placeholder="Search by title, description, or technology..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Filter Tags */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-gray-700">
            Filter by Tags
          </label>
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
        
        <div className="flex flex-wrap gap-1.5">
          {allTags.map((tag) => {
            const isSelected = selectedTags.includes(tag);
            return (
              <button
                key={tag}
                onClick={() => onTagToggle(tag)}
                className={`
                  inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                  ${isSelected
                    ? 'bg-blue-100 text-blue-800 border border-blue-200 shadow-sm'
                    : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 hover:border-gray-300'
                  }
                `}
              >
                {tag}
                {isSelected && (
                  <svg className="ml-1 h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
        
        {selectedTags.length > 0 && (
          <div className="mt-2 text-xs text-gray-500">
            {selectedTags.length} tag{selectedTags.length !== 1 ? 's' : ''} selected
          </div>
        )}
      </div>
    </div>
  );
}; 